import { lazy, Suspense, LazyExoticComponent, ComponentType } from "react";
import { RouteObject, Navigate } from "react-router-dom";
import { Loader } from "@aws-amplify/ui-react";

// Eager load critical components
import Home from "./pages/Home";
import LandingPageModern from "./pages/marketing/LandingPageModern";
import AuthPage from "./pages/marketing/AuthPage";
import MarketingLayout from "./components/MarketingLayout";
import DeveloperPortalLayout from "./components/DeveloperPortalLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import CompanyAdminRoute from "./components/CompanyAdminRoute";
import ProGate from "./components/ProGate";

// Lazy load page components
const Certifications = lazy(() => import("./pages/Certifications"));
const Skills = lazy(() => import("./pages/Skills"));
const AddCertification = lazy(() => import("./pages/AddCertification"));
const EditCertification = lazy(() => import("./pages/EditCertification"));
const SeedDataUnified = lazy(() => import("./pages/SeedDataUnified"));
const AddSkillPackage = lazy(() => import("./pages/AddSkillPackage"));
const SkillPackages = lazy(() => import("./pages/SkillPackages"));
const SkillPackageDiscovery = lazy(
  () => import("./pages/SkillPackageDiscovery"),
);
const SkillPackageDiscoveryModern = lazy(
  () => import("./pages/SkillPackageDiscoveryModern"),
);
const SkillPackageDetail = lazy(() => import("./pages/SkillPackageDetail"));
const CreateCustomSkillPackage = lazy(
  () => import("./pages/CreateCustomSkillPackage"),
);
const EditCustomSkillPackage = lazy(
  () => import("./pages/EditCustomSkillPackage"),
);
const SkillCategoryDetail = lazy(() => import("./pages/SkillCategoryDetail"));
const SkillCategorySkillDetail = lazy(
  () => import("./pages/SkillCategorySkillDetail"),
);
const SkillDetail = lazy(() => import("./pages/SkillDetail"));

// Settings pages
const Profile = lazy(() => import("./pages/settings/Profile"));
const Preferences = lazy(() => import("./pages/settings/Preferences"));
const Subscription = lazy(() => import("./pages/settings/Subscription"));
const CompanyEmployees = lazy(
  () => import("./pages/settings/CompanyEmployees"),
);
const Security = lazy(() => import("./pages/settings/Security"));
const SharingV2 = lazy(() => import("./pages/settings/SharingV2"));
const AIIntegrations = lazy(() => import("./pages/settings/AIIntegrations"));
const Notifications = lazy(() => import("./pages/settings/Notifications"));
const EnquiryHistory = lazy(() => import("./pages/settings/EnquiryHistory"));

// Sharing pages
const SharedSkills = lazy(() => import("./pages/SharedSkills"));
const SharedProfileView = lazy(() => import("./pages/SharedProfileView"));
const ShareInvitationAccept = lazy(
  () => import("./pages/ShareInvitationAccept"),
);

// Team pages
const TeamDetail = lazy(() => import("./pages/TeamDetail"));
const TeamAnalytics = lazy(() => import("./pages/TeamAnalytics"));

// Marketing pages
const SkillPackagesMarketing = lazy(
  () => import("./pages/marketing/SkillPackages"),
);
const FrameworksMarketing = lazy(() => import("./pages/marketing/Frameworks"));
const AboutUs = lazy(() => import("./pages/marketing/AboutUs"));
const DevelopersPage = lazy(() => import("./pages/marketing/DevelopersPage"));
const CommunityDiscovery = lazy(
  () => import("./pages/marketing/CommunityDiscovery"),
);

// Community package pages
const PackageDetail = lazy(() => import("./pages/PackageDetail"));

// Framework pages
const Frameworks = lazy(() => import("./pages/Frameworks"));
const FrameworksModern = lazy(() => import("./pages/FrameworksModern"));
const AddFramework = lazy(() => import("./pages/AddFramework"));
const FrameworkDiscovery = lazy(() => import("./pages/FrameworkDiscovery"));
const CreateFramework = lazy(() => import("./pages/CreateFramework"));
const EditFramework = lazy(() => import("./pages/EditFramework"));
const FrameworkDetail = lazy(() => import("./pages/FrameworkDetail"));
const FrameworkDetailModern = lazy(
  () => import("./pages/FrameworkDetailModern"),
);
const SharedFrameworkView = lazy(() => import("./pages/SharedFrameworkView"));
const Competencies = lazy(() => import("./pages/Competencies"));
const AddCompetency = lazy(() => import("./pages/AddCompetency"));
const CompetencyAssessment = lazy(() => import("./pages/CompetencyAssessment"));

// Admin pages
const ManageCompanySubscriptions = lazy(
  () => import("./pages/admin/ManageCompanySubscriptions"),
);
const MigrateCompetencies = lazy(
  () => import("./pages/admin/MigrateCompetencies"),
);
const PackageModerationDashboard = lazy(
  () => import("./pages/admin/PackageModerationDashboard"),
);

// Public pages
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Help = lazy(() => import("./pages/Help"));
const Contact = lazy(() => import("./pages/Contact"));
const EnquiryStatus = lazy(() => import("./pages/EnquiryStatus"));
const VerifyContactEnquiry = lazy(() => import("./pages/VerifyContactEnquiry"));
const PublicSkillsPage = lazy(() => import("./pages/PublicSkillsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const McpAuth = lazy(() => import("./pages/McpAuth"));

// Suggestions
const SuggestionInbox = lazy(() =>
  import("./components/SuggestionInbox").then((m) => ({ default: m.SuggestionInbox }))
);

// Demo pages
const SharedSkillsDemo = lazy(() => import("./pages/SharedSkillsDemo"));

// Loading fallback component
const LoadingFallback = () => (
  <div className="d-flex justify-center align-center" style={{ minHeight: "400px" }}>
    <Loader size="large" />
  </div>
);

// Wrapper to add Suspense to lazy components
const withSuspense = (Component: LazyExoticComponent<ComponentType>) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: "/app",
    element: <Home />,
  },
  {
    path: "/",
    element: (
      <MarketingLayout>
        <LandingPageModern />
      </MarketingLayout>
    ),
  },
  {
    path: "/login",
    element: <AuthPage initialState="signIn" />,
  },
  {
    path: "/signup",
    element: <AuthPage initialState="signUp" />,
  },
  {
    path: "/skill-packages",
    element: (
      <MarketingLayout>{withSuspense(SkillPackagesMarketing)}</MarketingLayout>
    ),
  },
  {
    path: "/frameworks",
    element: (
      <MarketingLayout>{withSuspense(FrameworksMarketing)}</MarketingLayout>
    ),
  },
  {
    path: "/about",
    element: <MarketingLayout>{withSuspense(AboutUs)}</MarketingLayout>,
  },
  {
    path: "/developer",
    element: <DeveloperPortalLayout>{withSuspense(DevelopersPage)}</DeveloperPortalLayout>,
  },
  {
    path: "/packages",
    element: (
      <MarketingLayout>{withSuspense(CommunityDiscovery)}</MarketingLayout>
    ),
  },
  {
    path: "/packages/:packageId",
    element: (
      <MarketingLayout>{withSuspense(PackageDetail)}</MarketingLayout>
    ),
  },
  {
    path: "/terms",
    element: withSuspense(Terms),
  },
  {
    path: "/privacy",
    element: withSuspense(Privacy),
  },
  {
    path: "/help",
    element: withSuspense(Help),
  },
  {
    path: "/demo/shared-skills",
    element: withSuspense(SharedSkillsDemo),
  },
  {
    path: "/contact",
    element: withSuspense(Contact),
  },
  {
    path: "/enquiry-status",
    element: withSuspense(EnquiryStatus),
  },
  {
    path: "/verify-contact",
    element: withSuspense(VerifyContactEnquiry),
  },
  {
    path: "/verify-email/:token",
    element: withSuspense(VerifyEmail),
  },
  {
    path: "/mcp-auth",
    element: withSuspense(McpAuth),
  },
  {
    path: "/profiles/:shareId",
    element: (
      <div className="position-relative" style={{ isolation: "isolate" }}>
        {withSuspense(PublicSkillsPage)}
      </div>
    ),
  },

  {
    path: "/app/skills",
    element: <ProtectedRoute>{withSuspense(Skills)}</ProtectedRoute>,
  },
  {
    path: "/app/skills/:skillId",
    element: <ProtectedRoute>{withSuspense(SkillDetail)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages",
    element: <ProtectedRoute>{withSuspense(SkillPackages)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages/discover",
    element: <ProtectedRoute>{withSuspense(SkillPackageDiscoveryModern)}</ProtectedRoute>,
  },
  {
    path: "/app/packages/:packageId",
    element: <ProtectedRoute>{withSuspense(PackageDetail)}</ProtectedRoute>,
  },  {
    path: "/app/skillpackages/discover-legacy",
    element: <ProtectedRoute>{withSuspense(SkillPackageDiscovery)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages/add",
    element: <ProtectedRoute>{withSuspense(AddSkillPackage)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages/create-custom",
    element: <ProtectedRoute>{withSuspense(CreateCustomSkillPackage)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages/edit-custom/:packageId",
    element: <ProtectedRoute>{withSuspense(EditCustomSkillPackage)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages/:packageId",
    element: <ProtectedRoute>{withSuspense(SkillPackageDetail)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages/categories/:categoryId",
    element: <ProtectedRoute>{withSuspense(SkillCategoryDetail)}</ProtectedRoute>,
  },
  {
    path: "/app/skillpackages/categories/skills/:skillId",
    element: <ProtectedRoute>{withSuspense(SkillCategorySkillDetail)}</ProtectedRoute>,
  },
  {
    path: "/app/certifications",
    element: <ProtectedRoute>{withSuspense(Certifications)}</ProtectedRoute>,
  },
  {
    path: "/app/certifications/add",
    element: <ProtectedRoute>{withSuspense(AddCertification)}</ProtectedRoute>,
  },
  {
    path: "/app/certifications/edit/:id",
    element: <ProtectedRoute>{withSuspense(EditCertification)}</ProtectedRoute>,
  },
  // Framework routes
  {
    path: "/app/frameworks",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(FrameworksModern)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/frameworks-legacy",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(Frameworks)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    // Redirect old "My Frameworks" route to consolidated Frameworks page
    path: "/app/frameworks/my",
    element: <Navigate to="/app/frameworks" replace />,
  },
  {
    path: "/app/frameworks/add",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(FrameworkDiscovery)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/frameworks/add-legacy",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(AddFramework)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/frameworks/create",
    element: (
      <ProtectedRoute>
        <CompanyAdminRoute>
          <ProGate>{withSuspense(CreateFramework)}</ProGate>
        </CompanyAdminRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/frameworks/:frameworkId",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(FrameworkDetailModern)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/frameworks/:frameworkId/legacy",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(FrameworkDetail)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/frameworks/:frameworkId/edit",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(EditFramework)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/competencies",
    element: <ProtectedRoute>{withSuspense(Competencies)}</ProtectedRoute>,
  },
  {
    path: "/app/competencies/add",
    element: <ProtectedRoute>{withSuspense(AddCompetency)}</ProtectedRoute>,
  },
  {
    path: "/app/competency-assessment",
    element: (
      <ProtectedRoute>{withSuspense(CompetencyAssessment)}</ProtectedRoute>
    ),
  },
  {
    path: "/certifications",
    element: <ProtectedRoute>{withSuspense(Certifications)}</ProtectedRoute>,
  },
  {
    path: "/certifications/add",
    element: <ProtectedRoute>{withSuspense(AddCertification)}</ProtectedRoute>,
  },
  {
    path: "/certifications/edit/:id",
    element: <ProtectedRoute>{withSuspense(EditCertification)}</ProtectedRoute>,
  },
  {
    path: "/skillpackages",
    element: <ProtectedRoute>{withSuspense(SkillPackages)}</ProtectedRoute>,
  },
  {
    path: "/skillpackages/add",
    element: <ProtectedRoute>{withSuspense(AddSkillPackage)}</ProtectedRoute>,
  },
  {
    path: "/skillpackages/create-custom",
    element: <ProtectedRoute>{withSuspense(CreateCustomSkillPackage)}</ProtectedRoute>,
  },
  {
    path: "/skillpackages/edit-custom/:packageId",
    element: <ProtectedRoute>{withSuspense(EditCustomSkillPackage)}</ProtectedRoute>,
  },
  {
    path: "/skillpackages/:packageId",
    element: <ProtectedRoute>{withSuspense(SkillPackageDetail)}</ProtectedRoute>,
  },
  {
    path: "/skillpackages/categories/:categoryId",
    element: <ProtectedRoute>{withSuspense(SkillCategoryDetail)}</ProtectedRoute>,
  },
  {
    path: "/admin/seed-data",
    element: <AdminRoute>{withSuspense(SeedDataUnified)}</AdminRoute>,
  },
  {
    path: "/admin/manage-company-subscriptions",
    element: (
      <AdminRoute>{withSuspense(ManageCompanySubscriptions)}</AdminRoute>
    ),
  },
  {
    path: "/admin/migrate-competencies",
    element: <AdminRoute>{withSuspense(MigrateCompetencies)}</AdminRoute>,
  },
  {
    path: "/app/admin/package-moderation",
    element: <AdminRoute>{withSuspense(PackageModerationDashboard)}</AdminRoute>,
  },

  // Settings routes
  {
    path: "/settings/profile",
    element: <ProtectedRoute>{withSuspense(Profile)}</ProtectedRoute>,
  },
  {
    path: "/settings/preferences",
    element: <ProtectedRoute>{withSuspense(Preferences)}</ProtectedRoute>,
  },
  {
    path: "/settings/subscription",
    element: <ProtectedRoute>{withSuspense(Subscription)}</ProtectedRoute>,
  },
  {
    path: "/settings/company-employees",
    element: <ProtectedRoute>{withSuspense(CompanyEmployees)}</ProtectedRoute>,
  },
  {
    path: "/settings/security",
    element: <ProtectedRoute>{withSuspense(Security)}</ProtectedRoute>,
  },
  {
    path: "/settings/sharing",
    element: <ProtectedRoute>{withSuspense(SharingV2)}</ProtectedRoute>,
  },
  {
    path: "/settings/integrations",
    element: <ProtectedRoute>{withSuspense(AIIntegrations)}</ProtectedRoute>,
  },
  {
    path: "/settings/notifications",
    element: <ProtectedRoute>{withSuspense(Notifications)}</ProtectedRoute>,
  },
  {
    path: "/settings/enquiry-history",
    element: <ProtectedRoute>{withSuspense(EnquiryHistory)}</ProtectedRoute>,
  },
  // Suggestions inbox
  {
    path: "/app/suggestions",
    element: <ProtectedRoute>{withSuspense(SuggestionInbox)}</ProtectedRoute>,
  },
  // Sharing routes
  {
    path: "/app/shared-skills",
    element: <ProtectedRoute>{withSuspense(SharedSkills)}</ProtectedRoute>,
  },
  {
    path: "/app/shared-skills/:profileId",
    element: <ProtectedRoute>{withSuspense(SharedProfileView)}</ProtectedRoute>,
  },
  {
    path: "/share/accept/:invitationId",
    element: withSuspense(ShareInvitationAccept),
  },
  {
    path: "/share/framework/:shareId",
    element: withSuspense(SharedFrameworkView),
  },
  // Team routes
  {
    path: "/app/teams/:teamId",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(TeamDetail)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "/app/teams/:teamId/analytics",
    element: (
      <ProtectedRoute>
        <ProGate>{withSuspense(TeamAnalytics)}</ProGate>
      </ProtectedRoute>
    ),
  },
  {
    path: "*",
    element: withSuspense(NotFound),
  },
];

export default routes;
